import React, { Component } from 'react';
import { observer } from 'mobx-react';
import { observable, action } from 'mobx';
import { Dropdown, Form } from 'semantic-ui-react';
import { debounce } from 'lodash';
import { api } from 'store/Base';

interface PhabricatorProject {
    phid: string;
    name: string;
    slug: string | null;
}

interface Props {
    model: any;
    /** Optional callback fired when the user picks/clears a project. */
    onPhidChange?: (phid: string | null) => void;
}

@observer
export default class PhabricatorProjectPicker extends Component<Props, any> {
    @observable options: { text: string; value: string }[] = [];
    @observable loading = false;
    // [T54191] Label of the currently selected project, remembered once we
    // have seen it (search results may not include it) so the picker shows
    // the project NAME, not the raw PHID.
    @observable currentLabel: string | null = null;

    componentDidMount() {
        // Load the full list eagerly so an already-linked project shows its
        // name right away (previously the list only loaded on first open and
        // the dropdown displayed the raw PHID until then).
        this.fetchProjects('');
    }

    fetchProjects = debounce(async (query: string) => {
        this.setLoading(true);
        try {
            const res = await api.get('subproject/search_phabricator_projects/', {
                q: query,
            });
            const results: PhabricatorProject[] = (res && res.results) || [];
            this.setOptions(results.map((p) => ({ text: p.name, value: p.phid })));
        } catch (e) {
            console.error('Failed to fetch Phabricator projects:', e);
        } finally {
            this.setLoading(false);
        }
    }, 400);

    @action setOptions(opts: { text: string; value: string }[]) {
        this.options = opts;
        // Remember the label of the currently selected project while we can
        // still see it in a (possibly filtered) result set.
        const cur = this.currentValue;
        if (cur) {
            const match = opts.find((o) => o.value === cur);
            if (match) this.currentLabel = match.text;
        }
    }

    @action setLoading(val: boolean) {
        this.loading = val;
    }

    // [T54341-followup] The search input is deliberately UNCONTROLLED: we do
    // not pass a `searchQuery` prop to the Dropdown. SUI clears its internal
    // searchQuery itself when an option is selected (clearSearchQuery), but a
    // controlled `searchQuery` prop always wins over that state
    // (ModernAutoControlledComponent), so the typed text stuck around after
    // selecting and hid the selected project's name (the `.text.filtered`
    // class keeps the label hidden while a search query is shown). This
    // mirrors spider's TargetSelect: it only wires `onSearchChange` and lets
    // the Dropdown own the query.
    handleSearchChange = (e: React.SyntheticEvent, { searchQuery: q }: any) => {
        this.fetchProjects(q);
    };

    // [T54341-followup] Reopening resets the options to the full list, like
    // TargetSelect does (its onOpen refetches with the — now empty — search
    // param), so the previous search doesn't keep the list filtered.
    handleOpen = () => {
        this.fetchProjects('');
    };

    handleChange = (e: React.SyntheticEvent, { value }: any) => {
        this.props.model.setInput('phabricatorProjectPhid', value || null);
        if (this.props.onPhidChange) {
            this.props.onPhidChange(value || null);
        }
    };

    get currentValue() {
        return this.props.model.phabricatorProjectPhid;
    }

    render() {
        let displayOptions = this.options;
        if (this.currentValue && !displayOptions.find((o) => o.value === this.currentValue)) {
            // Keep the selected project visible in the list. Use the remembered
            // name; only fall back to the raw PHID if it was never loaded
            // (e.g. the project was deleted in Phabricator).
            displayOptions = [{ text: this.currentLabel || this.currentValue, value: this.currentValue }, ...displayOptions];
        }

        return (
            <Form.Field>
                <label>Phabricator Project</label>
                <Dropdown
                    placeholder="Search Phabricator projects..."
                    fluid
                    search
                    selection
                    clearable
                    loading={this.loading}
                    options={displayOptions}
                    value={this.currentValue || ''}
                    onOpen={this.handleOpen}
                    onSearchChange={this.handleSearchChange}
                    onChange={this.handleChange}
                    noResultsMessage={this.loading ? 'Searching...' : 'No projects found'}
                />
            </Form.Field>
        );
    }
}
